import React, { useState, useEffect, useRef } from "react";
import { getExperiences } from "./api/experienceService";
import icons from './data/icons';
import { getAbout } from "./api/aboutService";

function About() {
  const visibleItems = useRef([]);
  const [visibleSet, setVisibleSet] = useState(new Set());
  const [experienceData, setExperienceData] = useState([]);
  const [activeFilter, setActiveFilter] = useState("");
  const [slideIndex, setSlideIndex] = useState(0);
  const sliderRef = useRef(null);
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [skillCategories, setSkillCategories] = useState({});
  const [educationData, setEducationData] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [skillLabels, setSkillLabels] = useState([]);
  const [aboutTitle, setAboutTitle] = useState({
    main: "",
    highlight: "",
  });

  const [profileImage, setProfileImage] = useState({
    src: "",
    alt: "",
    objectPosition: "",
  });

  const [academicTitle, setAcademicTitle] = useState({
    main: "",
    highlight: "",
  });
  const filterCategories = React.useMemo(() => {
    return Object.keys(skillCategories).map((key) => {
      const category = skillLabels.find(
        (item) => item.key === key
      );

      return {
        key,
        label: category?.label || key,
        count: skillCategories[key].length,
      };
    });
  }, [skillCategories, skillLabels]);
  useEffect(() => {
    if (
      !activeFilter &&
      filterCategories.length > 0
    ) {
      setActiveFilter(filterCategories[0].key);
    }
  }, [filterCategories, activeFilter]);
  const [bioData, setBioData] = useState("");
  const filterRef = useRef(null);
  const skillsRef = useRef(null);
  const MAX_VISIBLE = 3;

  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        const res = await getAbout();

        const data = res.data.data;

        const about = data.about?.[0];

        if (about) {
          setAboutTitle({
            main: about.aboutTitle,
            highlight: about.aboutHighlight,

          });

          setProfileImage({
            src: about.profileImage,
            alt: about.profileImageAlt,
            objectPosition: about.objectPosition,
          });

          setAcademicTitle({
            main: about.academicTitle,
            highlight: about.academicHighlight,
          });

          setBioData(about.bio);
          setHighlights(about.highlightWords || []);
          setSkillLabels(about.skillLabels || []);
        }

        const groupedSkills = (data.skills || []).reduce(
          (acc, skill) => {
            if (!acc[skill.category]) {
              acc[skill.category] = [];
            }

            acc[skill.category].push(skill);

            return acc;
          },
          {}
        );

        setSkillCategories(groupedSkills);

        setEducationData(data.education || []);

      } catch (error) {
        console.error("Failed to fetch About data:", error);
      }
    };

    fetchAboutData();
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getExperiences();
        setExperienceData(res.data.data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, []);
  const publicExperienceData = experienceData.filter(
    company =>
      company.positions?.some(
        position => position.countExperience !== false
      )
  );
  // ✅ SAME LOGIC AS PROJECTS
  const visibleFilters = React.useMemo(() => {
    if (showAllFilters) return filterCategories;

    const activeIndex = filterCategories.findIndex(
      f => f.key === activeFilter
    );

    // If active is already visible
    if (activeIndex < MAX_VISIBLE) {
      return filterCategories.slice(0, MAX_VISIBLE);
    }

    // Replace last item with active filter
    return [
      ...filterCategories.slice(0, MAX_VISIBLE - 1),
      filterCategories[activeIndex]
    ];
  }, [showAllFilters, activeFilter, filterCategories]);
  const hiddenCount = filterCategories.length - MAX_VISIBLE;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!showAllFilters) return;

      const clickedInsideFilters = filterRef.current?.contains(e.target);
      const clickedInsideSkills = skillsRef.current?.contains(e.target);

      // ❌ Ignore clicks on filters or skill cards
      if (clickedInsideFilters || clickedInsideSkills) return;

      // ✅ Collapse filters
      setShowAllFilters(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showAllFilters]);


  const parseDate = (str) => {
    if (!str) return null;
    if (str.toLowerCase().includes("present")) return new Date(9999, 11, 31);
    const [month, year] = str.split(" ");
    return new Date(`${month} 1, ${year}`);
  };


  function calculateCompletionTime(period) {
    if (!period) return "";
    const [startYear, endYear] = period.split(" - ").map(Number);
    if (isNaN(startYear) || isNaN(endYear)) return "";
    const years = endYear - startYear;
    return `${years} Years`;
  }


  const getCurrentPosition = () => {
    if (!publicExperienceData || publicExperienceData.length === 0) return null;


    let latestPosition = null;
    let latestEndDate = null;


    for (const company of publicExperienceData) {
      for (const position of company.positions || []) {
        if (!position.duration) continue;


        const [, endStr] = position.duration.split("-").map(s => s.trim());
        const endDate = parseDate(endStr);


        if (endStr.toLowerCase().includes("present")) {
          return {
            title: position.title,
            company: company.company,
            duration: position.duration
          };
        }


        if (!latestEndDate || endDate > latestEndDate) {
          latestEndDate = endDate;
          latestPosition = {
            title: position.title,
            company: company.company,
            duration: position.duration
          };
        }
      }
    }


    return latestPosition;
  };


  const currentPositions = publicExperienceData
    .flatMap(company =>
      company.positions
        .filter(pos => pos.duration.toLowerCase().includes("present"))
        .map(pos => ({
          ...pos,
          company: company.company,
          logo: company.logo,

        }))
    );


  const currentPosition = currentPositions.length > 0
    ? currentPositions.length > 1
      ? currentPositions[slideIndex % currentPositions.length]
      : currentPositions[0]
    : getCurrentPosition();


  const slides = React.useMemo(() => {
    if (!currentPositions || currentPositions.length === 0) {
      return currentPosition ? [currentPosition] : [];
    }


    if (currentPositions.length === 1) {
      return currentPositions;
    }


    return [...currentPositions, currentPositions[0]];
  }, [currentPositions, currentPosition]);


  useEffect(() => {
    if (currentPositions.length <= 1) return;


    const interval = setInterval(() => {
      setSlideIndex(prev => prev + 1);
    }, 3000);


    return () => clearInterval(interval);
  }, [currentPositions.length]);


  useEffect(() => {
    if (!sliderRef.current) return;


    if (currentPositions.length > 1 && slideIndex === currentPositions.length) {
      setTimeout(() => {
        if (sliderRef.current) {
          sliderRef.current.style.transition = "none";
        }
        setSlideIndex(0);


        requestAnimationFrame(() => {
          if (sliderRef.current) {
            sliderRef.current.style.transition = "transform 0.5s ease-out";
          }
        });
      }, 500);
    }
  }, [slideIndex, currentPositions.length]);


  const getLatestEducation = () => {
    if (!educationData || educationData.length === 0) return null;


    const withEndYear = educationData.map(ed => {
      const parts = ed.period.split('-').map(p => p.trim());
      const endYear = parseInt(parts[1] || parts[0], 10);
      return { ...ed, endYear: isNaN(endYear) ? 0 : endYear };
    });


    return withEndYear.reduce((latest, current) =>
      current.endYear > latest.endYear ? current : latest
    );
  };


  function getTotalExperience() {
    const parseDateLocal = (str) => {
      if (str.toLowerCase().includes("present")) return new Date();
      const [month, year] = str.split(" ");
      return new Date(`${month} 1, ${year}`);
    };


    const ranges = [];


    publicExperienceData.forEach((company) => {
      company.positions.forEach((pos) => {
        if (!pos.duration) return;


        const [startStr, endStr] = pos.duration.split("-").map(s => s.trim());


        ranges.push({
          start: parseDateLocal(startStr),
          end: parseDateLocal(endStr),
        });
      });
    });


    if (ranges.length === 0) return "0 months";


    ranges.sort((a, b) => a.start - b.start);


    const merged = [ranges[0]];


    for (let i = 1; i < ranges.length; i++) {
      const last = merged[merged.length - 1];
      const current = ranges[i];


      if (current.start <= last.end) {
        last.end = new Date(Math.max(last.end, current.end));
      } else {
        merged.push(current);
      }
    }


    let totalMonths = 0;


    merged.forEach(({ start, end }) => {
      totalMonths +=
        (end.getFullYear() - start.getFullYear()) * 12 +
        (end.getMonth() - start.getMonth()) +
        1;
    });


    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;


    const yearText =
      years > 0 ? `${years} ${years === 1 ? "year" : "years"}` : "";


    const monthText =
      months > 0 ? `${months} ${months === 1 ? "month" : "months"}` : "";


    return [yearText, monthText].filter(Boolean).join(" ");
  }



  const latestEdu = getLatestEducation();
  useEffect(() => {
    const timer = setTimeout(() => {
      const currentRefs = visibleItems.current.filter(
        (ref) => ref !== null
      );

      const observers = currentRefs.map((ref) => {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const refIndex =
                  visibleItems.current.indexOf(ref);

                setVisibleSet((prev) => {
                  const next = new Set(prev);
                  next.add(refIndex);
                  return next;
                });

                observer.unobserve(ref);
              }
            });
          },
          {
            threshold: 0.1,
            rootMargin: "0px 0px -50px 0px",
          }
        );

        observer.observe(ref);

        return observer;
      });

      return () => {
        observers.forEach((observer) =>
          observer.disconnect()
        );
      };
    }, 100);

    return () => clearTimeout(timer);
  }, [
    activeFilter,
    skillCategories,
    educationData,
  ]);



  const reversedSkillCategories = React.useMemo(
    () =>
      Object.entries(skillCategories).reduce((acc, [key, value]) => {
        acc[key] = [...value].reverse();
        return acc;
      }, {}),
    [skillCategories]
  );
  const FilterButtons = ({
    visibleCategories,
    activeKey,
    onChange,
    hiddenCount,
  }) => {


    return (
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 lg:gap-4 mb-8 sm:mb-12">
        {visibleCategories.map(category => (
          <button
            key={category.key}
            className={`relative inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-2.5 py-1 sm:py-1.5
      text-[10px] sm:text-xs lg:text-sm rounded-xl font-semibold whitespace-nowrap
      transition-all duration-300 hover:scale-105
      ${activeKey === category.key
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/50'
                : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 border border-slate-700/50'
              }`}
            onClick={() => onChange(category.key)}
          >
            {category.label}
            <span className={`inline-flex items-center justify-center min-w-[20px] sm:min-w-[24px] h-5 sm:h-6 px-1.5 sm:px-2 rounded-full text-[10px] sm:text-xs font-bold
      ${activeKey === category.key ? 'bg-white/20' : 'bg-slate-700/50'}`}>
              {category.count}
            </span>
          </button>
        ))}

        {!showAllFilters && hiddenCount > 0 && (
          <button
            onClick={() => setShowAllFilters(true)}
            className="px-2.5 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs lg:text-sm rounded-xl font-semibold
      bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 transition-all"
          >
            +{hiddenCount}
          </button>
        )}
      </div>
    );
  };


  // Render filtered content
  const renderSkills = () => {
    const itemsToShow = reversedSkillCategories[activeFilter] || [];

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
        {itemsToShow.map((item, index) => {
          const Icon = icons[item.icon];
          return (
            <div
              key={index}
              ref={el => visibleItems.current[index] = el}
              className={`flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-indigo-500/50 transition-all duration-300 transform hover:scale-105 ${visibleSet.has(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
            >
              <div className="text-3xl sm:text-4xl mb-1.5 sm:mb-2">
                <Icon color={item.color} />
              </div>
              <span className="text-xs sm:text-sm text-center text-slate-300">{item.name}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const reversedEducation = React.useMemo(
    () => [...educationData].reverse(),
    [educationData]
  );

  return (
    <section id="about" className="relative bg-[#0C121D] py-16 sm:py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-full mx-auto px-0 sm:px-0 lg:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl  font-bold text-white mb-4">
            {aboutTitle.main} <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">{aboutTitle.highlight}</span>
          </h2>
        </div>


        {/* Bio Section with Profile Image */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-8 mb-20">
          {/* Profile Image */}
          <div
            ref={el => (visibleItems.current[1000] = el)} // Changed from 0
            className={`lg:col-span-2 flex justify-center lg:justify-start transition-all duration-1000 ${visibleSet.has(1000) ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-32'
              }`}

          >
            <div className="flex items-center justify-center h-full">
              <div className="relative group w-fit mx-auto">

                {/* Glow */}
                <div
                  className="
        absolute
        inset-0
        bg-gradient-to-r
        from-indigo-600
        to-purple-600
        rounded-2xl
        blur-xl
        opacity-40
        group-hover:opacity-60
        transition
        duration-500
      "
                />

                {/* Image */}
                <img
                  src={profileImage.src}
                  alt={profileImage.alt}
                  className="
        relative

        w-64 h-80
        sm:w-72 sm:h-96
        md:w-80 md:h-[420px]

        rounded-2xl
        object-cover
        object-top

        border-4
        border-slate-800
        shadow-2xl
      "
                />

              </div>
            </div>
          </div>


          {/* Bio Text with Quick Stats */}
          <div
            ref={el => (visibleItems.current[1001] = el)} // Changed from 1
            className={`lg:col-span-3 flex flex-col justify-center transition-all duration-1000 ${visibleSet.has(1001) ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-32'
              }`}
          >
            <div className="bg-gradient-to-br from-slate-900/50 to-slate-800/50 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 hover:border-indigo-500/50 hover:shadow-lg hover:shadow-indigo-500/20 transition-all duration-500">
              {bioData
                ?.split(/\n\s*\n/)
                .map((paragraph, index) => {
                  const words = [
                    "{{experience}}",
                    ...highlights,
                  ];

                  const regex = new RegExp(
                    `(${words
                      .map((word) =>
                        word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
                      )
                      .join("|")})`,
                    "gi"
                  );

                  const parts = paragraph.split(regex);

                  return (
                    <p
                      key={index}
                      className="text-slate-300 text-sm sm:text-base lg:text-lg leading-relaxed mb-6"
                    >
                      {parts.map((part, i) => {
                        if (part === "{{experience}}") {
                          return (
                            <span
                              key={i}
                              className="text-indigo-400 font-semibold"
                            >
                              {getTotalExperience()}
                            </span>
                          );
                        }

                        const isHighlight = highlights.some(
                          (highlight) =>
                            highlight.toLowerCase() === part.toLowerCase()
                        );

                        if (isHighlight) {
                          return (
                            <span
                              key={i}
                              className="text-indigo-400 font-semibold"
                            >
                              {part}
                            </span>
                          );
                        }

                        return part;
                      })}
                    </p>
                  );
                })}


              {/* Quick Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 hover:border-indigo-500/50 transition-all duration-300">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center flex-shrink-0">
                      <icons.FaGraduationCap className="text-white text-lg sm:text-xl" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-[10px] sm:text-xs">Education</p>
                      <p className="text-white font-bold text-xs sm:text-sm">{latestEdu?.degree}</p>
                      <p className="text-cyan-400 text-[10px] sm:text-xs">{latestEdu?.institution}</p>
                      <p className="text-orange-400 text-[10px] sm:text-xs font-semibold mt-1">{latestEdu?.period}</p>
                    </div>
                  </div>
                </div>


                <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 hover:border-cyan-500/50 transition-all duration-300">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-cyan-600 to-blue-600 flex items-center justify-center flex-shrink-0">
                      {slides.length > 0 && slides[slideIndex]?.logo ? (
                        <img
                          src={slides[slideIndex].logo}
                          alt={slides[slideIndex].company || slides[slideIndex].title}
                          className="w-full h-full rounded-full bg-gradient-to-r from-cyan-600 to-blue-600 flex items-center justify-center"
                        />
                      ) : (
                        <icons.Briefcase className="text-white text-lg sm:text-xl" />
                      )}                    </div>
                    <div className="relative w-full">
                      <p className="text-slate-400 text-[10px] sm:text-xs mb-1">Experience</p>

                      {slides.length > 0 && (
                        <div className="relative overflow-hidden w-full">
                          <div
                            ref={sliderRef}
                            className="flex transition-transform duration-500 ease-out"
                            style={{
                              transform: `translateX(-${slideIndex * 100}%)`,
                            }}
                          >
                            {slides.map((pos, idx) => (
                              <div key={idx} className="min-w-full">
                                <p className="text-white font-bold text-xs sm:text-sm">{pos.title}</p>
                                <p className="text-cyan-300 text-[10px] sm:text-xs">{pos.company}</p>
                                <p className="text-orange-400 text-[10px] sm:text-xs font-semibold mt-1">{pos.duration}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {currentPositions.length > 1 && (
                        <div className="flex gap-1 mt-2">
                          {currentPositions.map((_, i) => (
                            <span
                              key={i}
                              className={`h-1 sm:h-1.5 rounded-full transition-all duration-300 ${i === slideIndex % currentPositions.length
                                ? 'bg-cyan-400 w-2.5 sm:w-3'
                                : 'bg-slate-600 w-1 sm:w-1.5'
                                }`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Education Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              {academicTitle.main} <span className="bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">{academicTitle.highlight}</span>
            </h3>
          </div>


          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reversedEducation.map((edu, index) => (
              <div
                key={index}
                ref={el => (visibleItems.current[100 + index] = el)}
                className={`
    bg-gradient-to-br from-slate-900/50 to-slate-800/50
    backdrop-blur-xl rounded-2xl p-6
    border border-slate-700/50
    hover:border-indigo-500/50
    hover:shadow-lg hover:shadow-indigo-500/20
    transition-all duration-700 ease-out
    ${visibleSet.has(100 + index)
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-10'}
  `}
              >

                <div className="flex items-start gap-3 mb-4">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r ${edu.gradient} flex items-center justify-center flex-shrink-0`}>
                    <icons.FaGraduationCap className="text-white text-lg sm:text-xl" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-base sm:text-lg">{edu.degree}</h4>
                    <p className={`font-semibold text-xs sm:text-sm ${index === 0 ? 'text-purple-300' :
                      index === 1 ? 'text-cyan-300' :
                        'text-purple-300'
                      }`}>
                      {edu.institution}
                    </p>
                  </div>
                </div>


                <div className="flex items-center gap-2 mb-3">
                  <icons.HiOutlineLocationMarker className="text-slate-400 text-xs sm:text-sm flex-shrink-0" />
                  <p className="text-slate-400 text-xs sm:text-sm">{edu.location}</p>
                </div>

                <div className="flex items-center justify-between mt-3 gap-2">
                  <div className="flex items-center gap-1.5">
                    <icons.Calendar className={`w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 ${index === 0 ? 'text-indigo-400' :
                      index === 1 ? 'text-cyan-400' :
                        'text-purple-400'
                      }`} />
                    <p className={`text-xs sm:text-sm font-medium ${index === 0 ? 'text-indigo-300' :
                      index === 1 ? 'text-cyan-300' :
                        'text-purple-300'
                      }`}>
                      {edu.period}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 text-[10px] sm:text-xs text-slate-400 bg-slate-800/50 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg">
                    <icons.FaClock className="text-orange-400 flex-shrink-0" />
                    <span>{calculateCompletionTime(edu.period)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>


        {/* Skills Section */}
        <div>
          <div ref={filterRef}>
            <FilterButtons
              visibleCategories={visibleFilters}
              activeKey={activeFilter}
              hiddenCount={hiddenCount}
              onChange={(key) => {
                setActiveFilter(key);
              }}
            />
          </div>

          {renderSkills()}
        </div>
      </div>
    </section>
  );
}


export default About;
